import React from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import "mdb-ui-kit/css/mdb.min.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { Input, Ripple, initMDB } from "mdb-ui-kit";
import "@fortawesome/fontawesome-free/css/all.min.css";
import FooterManual from "./footerManual";
import Notes from "./header";
import { useMediaQuery, useTheme } from "@mui/material";

initMDB({ Input, Ripple, initMDB });

// Constants for Color Palette - RED THEME
const RED = "#DC2626";
const DARK_RED_ACCENT = "#991B1B";
const LIGHT_RED_BG = "#FEF2F2";
const EXTRA_LIGHT_RED_HOVER = "#FFF5F5";
const BLACK_TEXT = "#1F2937";
const GRAY_TEXT = "#4B5563";

// Styled Paper Component
const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: "#fff",
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: "center",
    color: theme.palette.text.secondary,
    ...theme.applyStyles("dark", {
        backgroundColor: "#1A2027",
    }),
}));

// Service Card Component
const ServiceCard = ({ title, description, action, icon, gradient }) => (
    <Box
        sx={{
            background: gradient,
            borderRadius: "15px",
            padding: "25px",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            transition: "all 0.3s ease-in-out",
            boxShadow: "0 8px 25px rgba(220, 38, 38, 0.15)",
            border: `1px solid ${RED}20`,
            '&:hover': {
                transform: "translateY(-8px)",
                boxShadow: "0 15px 35px rgba(220, 38, 38, 0.25)",
            }
        }}
    >
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", marginBottom: "15px" }}>
                <Box
                    sx={{
                        backgroundColor: RED,
                        width: "50px",
                        height: "50px",
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginRight: "15px"
                    }}
                >
                    <i className={`fas fa-${icon}`} style={{ color: "white", fontSize: "20px" }}></i>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: "700", color: "white", flex: 1 }}>
                    {title}
                </Typography>
            </Box>
            <Typography sx={{ color: "white", fontSize: "14px", lineHeight: "1.6", marginBottom: "20px", opacity: 0.9 }}>
                {description}
            </Typography>
        </Box>
        <Box
            component="a"
            href={action}
            sx={{
                backgroundColor: "white",
                color: RED,
                padding: "10px 20px",
                borderRadius: "8px",
                textDecoration: "none",
                fontWeight: "600",
                fontSize: "14px",
                textAlign: "center",
                display: "block",
                transition: "all 0.3s ease",
                '&:hover': {
                    backgroundColor: LIGHT_RED_BG,
                    transform: "translateY(-2px)",
                }
            }}
        >
            Explore Now
        </Box>
    </Box>
);

// Value Card Component
const ValueCard = ({ title, description, icon }) => (
    <Box
        sx={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "25px",
            textAlign: "center",
            border: `2px solid ${RED}`,
            transition: "all 0.3s ease",
            height: "100%",
            '&:hover': {
                backgroundColor: LIGHT_RED_BG,
                transform: "translateY(-5px)",
                boxShadow: `0 10px 25px rgba(220, 38, 38, 0.15)`,
            }
        }}
    >
        <Box
            sx={{
                backgroundColor: LIGHT_RED_BG,
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 15px",
                border: `2px solid ${RED}`
            }}
        >
            <i className={`fas fa-${icon}`} style={{ color: RED, fontSize: "24px" }}></i>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: "700", color: BLACK_TEXT, marginBottom: "10px" }}>
            {title}
        </Typography>
        <Typography sx={{ color: GRAY_TEXT, fontSize: "14px", lineHeight: "1.5" }}>
            {description}
        </Typography>
    </Box>
);

const OurServices = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
    // Service Data
    const services = [
        {
            title: "New & Used Cars",
            description: "Discover excellence in motion! Explore our curated selection of new & used cars, merging style and performance seamlessly.",
            action: "https://car.amar.co.jp/",
            icon: "car",
            gradient: "linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)"
        },
        {
            title: "Modified Cars",
            description: "Unleash the extraordinary! Elevate your driving experience with our unique and customized modified cars.",
            action: "https://modified.amar.co.jp/",
            icon: "tachometer-alt",
            gradient: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)"
        },
        {
            title: "Auto Parts",
            description: "Drive with confidence. Explore our premium automobile parts collection – precision-engineered for peak performance.",
            action: "https://parts.amar.co.jp/",
            icon: "cogs",
            gradient: "linear-gradient(135deg, #F87171 0%, #EF4444 100%)"
        },
        {
            title: "Wheels",
            description: "Revolutionize your ride! Elevate style and performance with our high-quality wheels. Perfect blend of aesthetics and precision.",
            action: "https://wheels.amar.co.jp/",
            icon: "compass",
            gradient: "linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)"
        },
        {
            title: "High Quality Tires",
            description: "Master every road with confidence. Our high-quality tires redefine performance and safety for a smooth driving experience.",
            action: "https://tire.amar.co.jp/",
            icon: "life-ring",
            gradient: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)"
        },
        {
            title: "Scrap Solutions",
            description: "Transforming waste into value. Explore our diverse scrap collection—contributing to sustainable practices and eco-friendly solutions.",
            action: "https://scraps.amar.co.jp/",
            icon: "recycle",
            gradient: "linear-gradient(135deg, #F87171 0%, #EF4444 100%)"
        },
        {
            title: "Import & Export",
            description: "Global connections, local expertise. Seamlessly import and export with us. We handle container and cargo logistics for smooth trade.",
            action: "https://export.amar.co.jp/",
            icon: "globe-americas",
            gradient: "linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)"
        },
        {
            title: "Yahoo Auctions",
            description: "Secure your desires with us at Yahoo Auctions! Place your bids confidently for exclusive finds. Your winning bid awaits!",
            action: "https://auctions.yahoo.co.jp/seller/risingbean",
            icon: "gavel",
            gradient: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)"
        }
    ];

    // Values Data
    const values = [
        {
            title: "Quality Assurance",
            description: "We ensure every product, from a Used Car to a single part, meets top-notch quality and our internal standards for a worry-free purchase.",
            icon: "award"
        },
        {
            title: "Fast Delivery & Tracking",
            description: "Swift processing guarantees dispatch without delays. Online Order Tracking keeps you updated with real-time shipment status worldwide.",
            icon: "shipping-fast"
        },
        {
            title: "Budget Friendly",
            description: "Our efficient sourcing and streamlined processes ensure competitive, transparent pricing for Japanese exports, providing maximum value.",
            icon: "hand-holding-usd"
        },
        {
            title: "Huge Car Inventory",
            description: "Access to one of the largest selections of New and Used Japanese Cars, guaranteeing diverse choices across all major brands.",
            icon: "warehouse"
        },
        {
            title: "After Sale Service",
            description: "Our commitment extends past the port. We guarantee provision of all essential shipping and export documents for smooth customs clearance.",
            icon: "headset"
        },
        {
            title: "Customer & Sales Support",
            description: "Dedicated support (Mon-Sat, 9:00-17:00 JST) ensures prompt communication, expert guidance, and transparent pricing with No Hidden Charges.",
            icon: "users"
        }
    ];

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                minHeight: "100vh",
                backgroundColor: "#fff"
            }}
        >
            {/* Header Component */}
            <Box sx={{ marginBottom: "0%" }}><Notes /></Box>

            {/* Hero Banner Section */}
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    height: isMobile ? "25vh" : isTablet ? "35vh" : "50vh",
                    backgroundColor: "black",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: "hidden"
                }}
            >
                <Box
                    component="img"
                    src="/image/AllSeeModelsBackgroungCar.png"
                    alt="Automotive Background"
                    sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        opacity: 0.4,
                    }}
                />

                <Box sx={{ position: "absolute", textAlign: "center", color: "white", maxWidth: "800px", padding: "20px" }}>
                    <Typography
                        variant="h1"
                        sx={{
                            fontWeight: "900",
                            textTransform: "uppercase",
                            letterSpacing: "3px",
                            // fontSize: { xs: "32px", md: "48px", lg: "56px" },
                            fontSize: isMobile ? "24px" : isTablet ? "42px" : "60px",
                            marginBottom: "20px",
                            textShadow: "2px 2px 8px rgba(0,0,0,0.8)"
                        }}
                    >
                        Your Global Automotive Partner
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: "300",
                            fontSize: { xs: "16px", md: "20px" },
                            marginBottom: "30px",
                            opacity: 0.9
                        }}
                    >
                        17 Years of Excellence in Japanese Car Export
                    </Typography>
                    <Box
                        onClick={() => window.location.href = 'https://car.amar.co.jp/'}
                        sx={{
                            backgroundColor: RED,
                            color: "white",
                            padding: "12px 30px",
                            borderRadius: "25px",
                            fontWeight: "600",
                            fontSize: "16px",
                            display: "inline-block",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            '&:hover': {
                                backgroundColor: DARK_RED_ACCENT,
                                transform: "translateY(-2px)",
                            }
                        }}
                    >
                        Discover Our Services
                    </Box>
                </Box>
            </Box>

            {/* Main Content Container */}
            <Box
                sx={{
                    width: { xs: "95%", lg: "90%" },
                    backgroundColor: "#FFFFFF",
                    position: "relative",
                    margin: "0 auto",
                    padding: { xs: "20px", md: "40px" },
                }}
            >

                {/* Introduction Section */}
                <Box sx={{ textAlign: "center", marginBottom: "50px", padding: "0 20px" }}>
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: "700",
                            color: RED,
                            marginBottom: "20px",
                            fontSize: { xs: "28px", md: "36px" }
                        }}
                    >
                        Excellence in Japanese Automotive Export
                    </Typography>
                    <Typography
                        sx={{
                            color: GRAY_TEXT,
                            fontSize: "18px",
                            lineHeight: "1.7",
                            maxWidth: "900px",
                            margin: "0 auto"
                        }}
                    >
                        Amar Enterprises Co Ltd is committed to being your comprehensive solution for sourcing and exporting
                        quality Japanese New & Used Cars, Auto Parts, and Scrap. With a 17-year legacy based in Saitama, Japan,
                        we deliver unmatched value, efficiency, and trust worldwide.
                    </Typography>
                </Box>

                {/* Core Services Section - PREMIUM VERSION */}
                <Box sx={{ marginBottom: "80px" }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: "700",
                            color: BLACK_TEXT,
                            textAlign: "center",
                            marginBottom: "40px",
                            position: "relative",
                            paddingBottom: "20px",
                            '&:after': {
                                content: '""',
                                position: "absolute",
                                bottom: "0",
                                left: "50%",
                                transform: "translateX(-50%)",
                                width: "100px",
                                height: "4px",
                                background: `linear-gradient(90deg, ${RED}, ${DARK_RED_ACCENT})`,
                                borderRadius: "2px"
                            }
                        }}
                    >
                        Our Core Services
                    </Typography>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr 1fr" },
                            gap: "30px",
                        }}
                    >
                        {services.map((service, index) => (
                            <Box
                                key={index}
                                sx={{
                                    background: "linear-gradient(135deg, #FFFFFF 0%, #FEF2F2 100%)",
                                    borderRadius: "20px",
                                    padding: "30px 25px",
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    textAlign: "center",
                                    transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                                    boxShadow: `
                        0 4px 6px rgba(220, 38, 38, 0.05),
                        0 1px 3px rgba(220, 38, 38, 0.1)
                    `,
                                    border: `1px solid ${LIGHT_RED_BG}`,
                                    position: "relative",
                                    overflow: "hidden",
                                    '&:before': {
                                        content: '""',
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: "4px",
                                        background: `linear-gradient(90deg, ${RED}, ${DARK_RED_ACCENT})`,
                                        transform: "scaleX(0)",
                                        transition: "transform 0.4s ease"
                                    },
                                    '&:hover': {
                                        transform: "translateY(-10px) scale(1.02)",
                                        boxShadow: `
                            0 20px 40px rgba(220, 38, 38, 0.15),
                            0 8px 20px rgba(220, 38, 38, 0.1)
                        `,
                                        '&:before': {
                                            transform: "scaleX(1)"
                                        },
                                        '& .service-icon-wrapper': {
                                            transform: "translateY(-5px)",
                                            boxShadow: `0 10px 25px rgba(220, 38, 38, 0.3)`
                                        }
                                    }
                                }}
                            >
                                {/* Icon with Background */}
                                <Box
                                    className="service-icon-wrapper"
                                    sx={{
                                        background: `linear-gradient(135deg, ${RED}, ${DARK_RED_ACCENT})`,
                                        width: "90px",
                                        height: "90px",
                                        borderRadius: "22px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginBottom: "25px",
                                        transition: "all 0.4s ease",
                                        boxShadow: `0 6px 15px rgba(220, 38, 38, 0.3)`
                                    }}
                                >
                                    <i className={`fas fa-${service.icon}`} style={{ color: "white", fontSize: "36px" }}></i>
                                </Box>

                                {/* Service Title */}
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: "700",
                                        color: BLACK_TEXT,
                                        marginBottom: "15px",
                                        fontSize: "19px",
                                        lineHeight: "1.3"
                                    }}
                                >
                                    {service.title}
                                </Typography>

                                {/* Service Description */}
                                <Typography
                                    sx={{
                                        color: GRAY_TEXT,
                                        fontSize: "14px",
                                        lineHeight: "1.6",
                                        marginBottom: "25px",
                                        flex: 1
                                    }}
                                >
                                    {service.description}
                                </Typography>

                                {/* Action Button */}
                                {/* <Box
                                    component="a"
                                    href={service.action}
                                    sx={{
                                        background: "transparent",
                                        color: RED,
                                        padding: "12px 30px",
                                        borderRadius: "50px",
                                        textDecoration: "none",
                                        fontWeight: "600",
                                        fontSize: "14px",
                                        textAlign: "center",
                                        display: "block",
                                        width: "100%",
                                        transition: "all 0.3s ease",
                                        border: `2px solid ${RED}`,
                                        position: "relative",
                                        overflow: "hidden",
                                        '&:before': {
                                            content: '""',
                                            position: "absolute",
                                            top: 0,
                                            left: "-100%",
                                            width: "100%",
                                            height: "100%",
                                            background: `linear-gradient(90deg, transparent, rgba(220, 38, 38, 0.1), transparent)`,
                                            transition: "left 0.5s ease"
                                        },
                                        '&:hover': {
                                            backgroundColor: RED,
                                            color: "white",
                                            transform: "translateY(-2px)",
                                            boxShadow: `0 8px 20px rgba(220, 38, 38, 0.3)`,
                                            '&:before': {
                                                left: "100%"
                                            }
                                        }
                                    }}
                                >
                                    Discover More
                                </Box> */}
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Trusted Values Section */}
                <Box sx={{ marginBottom: "80px" }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: "700",
                            color: BLACK_TEXT,
                            textAlign: "center",
                            marginBottom: "40px",
                            position: "relative",
                            '&:after': {
                                content: '""',
                                position: "absolute",
                                bottom: "-10px",
                                left: "50%",
                                transform: "translateX(-50%)",
                                width: "80px",
                                height: "4px",
                                backgroundColor: RED,
                                borderRadius: "2px"
                            }
                        }}
                    >
                        Our Trusted Values
                    </Typography>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" },
                            gap: "30px",
                            marginTop: "40px"
                        }}
                    >
                        {values.map((value, index) => (
                            <ValueCard
                                key={index}
                                title={value.title}
                                description={value.description}
                                icon={value.icon}
                            />
                        ))}
                    </Box>
                </Box>

                {/* Trust & Compliance Section */}
                <Box
                    sx={{
                        backgroundColor: LIGHT_RED_BG,
                        borderRadius: "20px",
                        padding: { xs: "25px", md: "40px" },
                        marginBottom: "50px",
                        border: `2px solid ${RED}30`
                    }}
                >
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                            gap: "40px",
                            alignItems: "center"
                        }}
                    >
                        {/* Legal Compliance */}
                        <Box>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: "700",
                                    color: RED,
                                    marginBottom: "20px",
                                    display: "flex",
                                    alignItems: "center"
                                }}
                            >
                                <i className="fas fa-shield-alt" style={{ marginRight: "10px" }}></i>
                                Trust & Legal Compliance
                            </Typography>
                            <Typography sx={{ color: GRAY_TEXT, marginBottom: "15px", lineHeight: "1.6" }}>
                                Amar Enterprises Co Ltd has a 17-year legacy operating under strict Japanese commercial law.
                                Our official license as a secondhand goods dealer is:
                            </Typography>
                            <Box
                                sx={{
                                    backgroundColor: "white",
                                    padding: "15px",
                                    borderRadius: "10px",
                                    border: `1px solid ${RED}40`
                                }}
                            >
                                <Typography sx={{ color: RED, fontWeight: "600", fontSize: "14px" }}>
                                    Saitama Prefectural Public Safety Commission No. 431260012433, Qualification Registration Number: T7-0300-0202-3122
                                </Typography>
                            </Box>
                            <Typography sx={{ color: GRAY_TEXT, marginTop: "15px", lineHeight: "1.6" }}>
                                We ensure every transaction adheres to Japan's export and business regulations, providing you
                                with a safe and secure purchasing channel.
                            </Typography>
                        </Box>

                        {/* Global Shipping */}
                        <Box>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: "700",
                                    color: RED,
                                    marginBottom: "20px",
                                    display: "flex",
                                    alignItems: "center"
                                }}
                            >
                                <i className="fas fa-shipping-fast" style={{ marginRight: "10px" }}></i>
                                Global Shipping Reach
                            </Typography>
                            <Typography sx={{ color: GRAY_TEXT, marginBottom: "20px", lineHeight: "1.6" }}>
                                As specialists in international logistics, we handle complex shipping requirements worldwide.
                                We utilize both container and RORO cargo logistics, reaching destinations across:
                            </Typography>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "10px"
                                }}
                            >
                                {["Africa", "Oceania", "Asia", "Europe", "Americas", "Middle East"].map((region, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            color: GRAY_TEXT,
                                            fontSize: "14px"
                                        }}
                                    >
                                        <i className="fas fa-check" style={{ color: RED, marginRight: "8px", fontSize: "12px" }}></i>
                                        {region}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* Call to Action */}
                <Box
                    sx={{
                        textAlign: "center",
                        padding: "50px 20px",
                        backgroundColor: "white",
                        borderRadius: "20px",
                        border: `3px solid ${RED}20`
                    }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: "700",
                            color: BLACK_TEXT,
                            marginBottom: "20px"
                        }}
                    >
                        Ready to Find Your Perfect Vehicle?
                    </Typography>
                    <Typography
                        sx={{
                            color: GRAY_TEXT,
                            fontSize: "18px",
                            marginBottom: "30px",
                            maxWidth: "600px",
                            margin: "0 auto 30px"
                        }}
                    >
                        Join thousands of satisfied customers worldwide who trust Amar Enterprises for their automotive needs.
                    </Typography>
                    <Box
                        sx={{
                            backgroundColor: RED,
                            color: "white",
                            padding: "15px 40px",
                            borderRadius: "30px",
                            fontWeight: "600",
                            fontSize: "16px",
                            display: "inline-block",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            '&:hover': {
                                backgroundColor: DARK_RED_ACCENT,
                                transform: "translateY(-3px)",
                                boxShadow: `0 10px 25px rgba(220, 38, 38, 0.3)`,
                            }
                        }}
                    >
                        Start Your Journey Today
                    </Box>
                </Box>

            </Box>

            {/* Space Filler for Footer Alignment */}
            <Box sx={{ height: isMobile ? "10px" : isTablet ? "10px" : "50px" }} />

            {/* Footer Component */}
            <FooterManual />
        </Box>
    );
};

export default OurServices;